import type { LandingPageContent } from './types'

export const PHOTOVOLTAIK_OHNE_TELEFONNUMMER: LandingPageContent = {
  slug: 'photovoltaik-ohne-telefonnummer',
  lang: 'de',
  topicId: 'photovoltaik-ohne-telefonnummer',
  title: 'Photovoltaik-Angebote ohne Telefonnummer – 100% anonym vergleichen',
  metaDescription:
    'Photovoltaik-Angebote anfragen ganz ohne Telefonnummer oder E-Mail: anonyme Anfrage, kein ' +
    'Anruf-Spam, Kontakt nur nach Ihrer Freigabe. Kostenlos & unverbindlich.',
  keywords: [
    'Photovoltaik ohne Telefonnummer',
    'Photovoltaik ohne E-Mail',
    'Solaranlage anfragen ohne Anruf',
    'Anonymer Photovoltaik Vergleich',
    'PV Angebot ohne Kaltakquise',
    'Photovoltaik Anfrage ohne Registrierung',
    'kein Anruf-Spam Solaranlage',
    'Vertreter Photovoltaik loswerden',
    'Haustürgeschäft Photovoltaik vermeiden',
  ],
  eyebrow: 'Anonym & ohne Anruf-Spam',
  h1: 'Photovoltaik-Angebote einholen – ganz ohne Telefonnummer oder E-Mail',
  intro:
    'Auf den meisten Vergleichsportalen ist eine Telefonnummer oder E-Mail-Adresse Pflicht, bevor ' +
    'Sie überhaupt ein Angebot sehen - und genau die Daten werden anschließend an mehrere Firmen ' +
    'gleichzeitig weitergereicht. Bei Roofora funktioniert die komplette Anfrage ohne beides: Sie ' +
    'vergleichen Photovoltaik-Angebote, ohne dass ein einziger Handwerksbetrieb Sie anrufen kann, ' +
    'bevor Sie das ausdrücklich erlauben.',
  sections: [
    { type: 'heading', text: 'Warum Telefonnummer und E-Mail bei anderen Portalen zum Problem werden' },
    {
      type: 'paragraph',
      text:
        'Sobald eine Anfrage mit echten Kontaktdaten einmal veröffentlicht ist, entscheiden Sie ' +
        'nicht mehr, wer sie sieht. Viele Portale verkaufen dieselbe Anfrage an mehrere Firmen ' +
        'gleichzeitig - die Folge sind Anrufe von Vertrieblern, die Sie nie kontaktiert haben, oft ' +
        'schon wenige Minuten nach dem Absenden.',
    },
    { type: 'heading', text: 'So funktioniert die Anfrage komplett anonym bei Roofora' },
    {
      type: 'paragraph',
      text:
        'Ihr Account entsteht nur mit einem selbst gewählten Benutzernamen und Passwort, dazu ' +
        'einem Recovery-Code fürs Zurücksetzen - keine E-Mail-Adresse, keine Telefonnummer. Ihre ' +
        'Anfrage (Dachbilder, Zählerschrank-Foto, Ausrichtung, Verbrauch) wird anonymisiert ' +
        'veröffentlicht: Handwerksbetriebe sehen PLZ-Gebiet und Eckdaten, aber keine Kontaktdaten.',
    },
    { type: 'heading', text: 'Wann ein Handwerksbetrieb überhaupt Kontakt aufnehmen darf' },
    {
      type: 'paragraph',
      text:
        'Handwerksbetriebe können unverbindlich ein Angebot abgeben oder eine Kontaktanfrage ' +
        'stellen - beides bekommen Sie nur als anonyme Benachrichtigung. Erst wenn Sie einen ' +
        'einzelnen Betrieb aktiv und einzeln freigeben, erhält genau dieser Betrieb Ihre ' +
        'Telefonnummer und E-Mail-Adresse. Keine Sammelfreigabe, kein Automatismus.',
    },
    {
      type: 'list',
      items: [
        'Kein Anruf bei Registrierung oder Angebotsabgabe möglich',
        'Kein Weiterverkauf Ihrer Daten an Dritte',
        'Sie legen ein Limit fest, wie viele Betriebe maximal Kontaktdaten erhalten dürfen',
        'Jede Freigabe gilt nur für genau einen von Ihnen ausgewählten Betrieb',
        'Angebote vergleichen Sie in Ruhe, ganz ohne Zeitdruck durch Anrufe',
      ],
    },
    { type: 'heading', text: 'Für wen sich die anonyme Anfrage besonders lohnt' },
    {
      type: 'paragraph',
      text:
        'Wenn Sie schon einmal nach einem einzigen Angebot tagelang von verschiedenen Firmen ' +
        'angerufen wurden, kennen Sie das Problem. Die anonyme Anfrage löst genau das: Sie bleiben ' +
        'so lange unerreichbar für Vertriebsanrufe, bis Sie selbst aktiv den ersten Schritt machen.',
    },
  ],
  faq: [
    {
      q: 'Muss ich wirklich keine Telefonnummer angeben?',
      a: 'Nein. Für den Kunden-Account ist weder eine Telefonnummer noch eine E-Mail-Adresse nötig - Sie registrieren sich nur mit Benutzername und Passwort.',
    },
    {
      q: 'Wie kann ich mich dann überhaupt registrieren oder mein Passwort zurücksetzen?',
      a: 'Sie erhalten bei der Registrierung einen Recovery-Code, mit dem Sie Ihr Passwort später zurücksetzen können - ganz ohne E-Mail-Postfach oder SMS.',
    },
    {
      q: 'Was, wenn ich doch von einem bestimmten Handwerksbetrieb kontaktiert werden möchte?',
      a: 'Sie können jederzeit gezielt einen einzelnen Betrieb freigeben - erst dann erhält genau dieser Betrieb Ihre Telefonnummer und E-Mail-Adresse, alle anderen weiterhin nicht.',
    },
    {
      q: 'Wie werde ich die Kaltakquise anderer Photovoltaik-Anbieter los?',
      a: 'Bei Roofora entsteht das Problem gar nicht erst: Ihre Kontaktdaten werden nur an Betriebe weitergegeben, die Sie selbst einzeln freigeben. Bei bereits laufenden unerwünschten Anrufen anderer Anbieter hilft es, keine sensiblen Daten am Telefon preiszugeben und im Zweifel aufzulegen.',
    },
    {
      q: 'Wie vermeide ich ein Haustürgeschäft bei Photovoltaik?',
      a: 'Am sichersten, indem Sie Angebote von vornherein über eine anonymisierte Anfrage einholen statt über Vertreter an der Haustür oder am Telefon - so entscheiden allein Sie, welcher Betrieb überhaupt Kontakt zu Ihnen aufnehmen darf.',
    },
  ],
  primaryCta: { label: 'Jetzt anonym Angebote anfragen', action: 'customer' },
  secondaryCta: { label: 'Wie der Angebotsvergleich funktioniert', action: 'link', href: '/pv-angebot-vergleichen' },
  relatedLinks: [
    { label: 'So funktioniert der Angebotsvergleich', href: '/pv-angebot-vergleichen' },
    { label: 'Was kostet eine Solaranlage? Preise & Vergleich', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Anfrage in wenigen Minuten erstellen', href: '/photovoltaik-anfrage-erstellen' },
    { label: 'Warum Roofora auf E-Mail verzichtet', href: '/blog/warum-keine-email-registrierung' },
    { label: 'PV-Kostenrechner: Durchschnittspreis in Sekunden', href: '/pv-kostenrechner' },
  ],
}
